import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GetRoleList, DeleteRole, UpdateRoleStatus } from '../../../../Api/role';
import SearchBox from '../../../../Components/SearchBox';
import Pagination from '../../../../Utils';
import { Formik } from 'formik';
import { ErrorToast, SuccessToast, Confirmation } from '../../../../Utils/SweetAlert';
import AddButton from '../../../../Components/AddButton';
import { EditButton, DeleteButton, StatusButton } from '../../../../Components';

const RoleList = () => {
    const navigate = useNavigate();
    const formRef = useRef();

    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);

    const {
        isFetching: roleDataLoading,
        data: roleData,
        refetch,
    } = useQuery({
        queryKey: ['get-role-list', page, limit],
        queryFn: () =>
            GetRoleList({
                limit: limit,
                page: page === 0 ? 1 : page + 1,
                search: formRef?.current?.values?.search,
            }),
        throwOnError: (error) => {
            ErrorToast(error?.response?.data?.message || 'Server Error.');
        },
    });

    const handlePageChange = async (e, pageNew) => {
        setPage(pageNew);
        refetch();
    };

    const handleLimitChange = async (event) => {
        setPage(0);
        setLimit(parseInt(event.target.value, 10));
        refetch();
    };

    return (
        <>
            <Helmet>
                <title>OVE | Roles</title>
            </Helmet>
            <div className="page-wrapper role-page-wrapper">
                <div className="content container-fluid">
                    <div className="admin-wrapper">
                        <div className="page-header">
                            <div className="col">
                                <h1 className="page-title">Roles</h1>
                            </div>
                            <div className="col-12 col-md-3 col-lg-2 px-2">
                                <Formik initialValues={{ search: '' }} innerRef={formRef}>
                                    {() => (
                                        <form>
                                            <SearchBox refetch={refetch} setPage={setPage} />
                                        </form>
                                    )}
                                </Formik>
                            </div>
                            <div>
                                <AddButton
                                    to="/admin/add-role"
                                    permission="create-role"
                                />
                            </div>
                        </div>
                        <div className="col-sm-12 table-width">
                            <div className="cards-table card-table">
                                <div className="card-body">
                                    <div className="table-responsive role-table custom-pagination">
                                        <table className="table datatable table-manager mb-0">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th className="col-name">Name</th>
                                                    <th className="col-status">Status</th>
                                                    <th className="col-action">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {roleDataLoading && (
                                                    <tr>
                                                        <td align="center" colSpan={3}>
                                                            <div className="d-flex justify-content-center align-items-center py-4">
                                                                <div className="spinner-border loader-theme" role="status" />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                                {!roleDataLoading && roleData !== undefined && (
                                                    <>
                                                        {roleData?.data?.items?.map((item, index) => {
                                                            return (
                                                                <tr
                                                                    key={index}
                                                                    className="table mb-0"
                                                                >
                                                                    <td className="col-name">
                                                                        <span className="role-cell-ellipsis">{item?.name || '-'}</span>
                                                                    </td>
                                                                    <td className="col-status">
                                                                        <StatusButton
                                                                            status={item?.status}
                                                                            onStatusChange={(status) => {
                                                                                Confirmation(
                                                                                    status === true
                                                                                        ? "Are you sure you want to active role status ?"
                                                                                        : "Are you sure you want to inactive role status?"
                                                                                ).then((result) => {
                                                                                    if (result.isConfirmed) {
                                                                                        (async () => {
                                                                                            try {
                                                                                                const res = await UpdateRoleStatus({ id: item?._id });
                                                                                                if (res?.status === 1) {
                                                                                                    SuccessToast(res?.message || 'Status Updated');
                                                                                                    refetch();
                                                                                                } else {
                                                                                                    ErrorToast(res?.message || 'Server Error.');
                                                                                                }
                                                                                            } catch (error) {
                                                                                                ErrorToast(error?.response?.data?.message || 'Server Error.');
                                                                                            }
                                                                                        })();
                                                                                    }
                                                                                });
                                                                            }}
                                                                            editPermission="edit-role"
                                                                        />
                                                                    </td>
                                                                    <td className="role-user-td-padding-remove col-action">
                                                                        <EditButton
                                                                            to="/admin/edit-role"
                                                                            state={{ id: item?._id }}
                                                                            permission="edit-role"
                                                                        />
                                                                        <DeleteButton
                                                                            onDelete={() => {
                                                                                Confirmation('Are you sure you want to delete role?').then((result) => {
                                                                                    if (result.isConfirmed) {
                                                                                        (async () => {
                                                                                            try {
                                                                                                const res = await DeleteRole(item?._id);
                                                                                                if (res?.status === 1) {
                                                                                                    SuccessToast(res?.message || 'Delete Successful.');
                                                                                                    refetch();
                                                                                                } else {
                                                                                                    ErrorToast(res?.message || 'Server Error.');
                                                                                                }
                                                                                            } catch (error) {
                                                                                                ErrorToast(error?.response?.data?.message || 'Server Error.');
                                                                                            }
                                                                                        })();
                                                                                    }
                                                                                });
                                                                            }}
                                                                            permission="delete-role"
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {roleData?.data?.items?.length === 0 && (
                                                            <tr>
                                                                <td className="text-center" colSpan={3}>
                                                                    No Records Found.
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Pagination
                            totalItem={roleData?.data?.totalItems || 0}
                            itemsPerPage={limit}
                            page={
                                !roleData?.data?.totalItems ||
                                    roleData?.data?.totalItems <= 0
                                    ? 0
                                    : page
                            }
                            handleChangePage={handlePageChange}
                            handleChangeRowsPerPage={handleLimitChange}
                            colSpan={3}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default RoleList;


